import { ComponentProps, FC, PropsWithChildren, ReactNode } from 'react'
import styled from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media, mediaMax } from 'styles/media'
import typeStyle from 'styles/typeStyle'

import ButtonLink from '../display/ButtonLink'
import BaseContainer from '../layout/Container'
import LinkOrA from '../cards/shared/LinkOrA'

export const Container = styled(BaseContainer)`
  margin: 60px auto;
  padding: 0 30px;

  & > * {
    margin-left: auto;
    margin-right: auto;
  }

  ${media.md`
    margin: 120px auto;
  `}
`

const PageCTALinkOuter = styled.div<{ noMarginTop?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  margin: ${({ noMarginTop }) => (noMarginTop ? '0' : '60px')} 0 60px 0;

  ${media.md`
    margin: ${({ noMarginTop }: { noMarginTop?: boolean }) =>
      noMarginTop ? '0' : '100px'} 0 100px 0;
  `}

  & > *:not(:last-child) {
    margin-bottom: 30px;
  }
`

const PageCTALinkInner = styled.div`
  display: flex;
  justify-content: center;

  & > * {
    font-size: 20px;
  }

  ${ButtonLink} {
    width: 100%;
    font-size: inherit;

    ${media.md`
      width: auto;
    `}
  }
`

type PageCTALinkProps = {
  info?: ReactNode
  noMarginTop?: boolean
} & ComponentProps<typeof ButtonLink>

export const PageCTALink: FC<PropsWithChildren<PageCTALinkProps>> = ({
  href,
  children,
  info,
  noMarginTop,
  ...props
}) => {
  return (
    <PageCTALinkOuter noMarginTop={noMarginTop}>
      {info}
      <PageCTALinkInner>
        <ButtonLink href={href} rounded {...props}>
          {children}
        </ButtonLink>
      </PageCTALinkInner>
    </PageCTALinkOuter>
  )
}

const ComponentCTALinkInner = styled.span`
  ${typeStyle('-ds-font-body-light')}
  color: ${({ theme }) => theme.brand.jungle};
`

export const ComponentCTALink: FC<
  PropsWithChildren<{
    url: string
  }>
> = ({ url, children }) => {
  const { md: largerThanMd } = useMediaQueryContext()
  const isSmallerThanMd = !largerThanMd

  return (
    <LinkOrA asLink={isSmallerThanMd} to={url}>
      <ComponentCTALinkInner>{children}</ComponentCTALinkInner>
    </LinkOrA>
  )
}

type ResTwoColProps = {
  columnMarginBottom?: number
  gutter?: number | string
}

export const ResponsiveTwoColumnLayout = styled.div<ResTwoColProps>`
  display: flex;
  justify-content: center;

  ${mediaMax.md`
    flex-direction: column;
  `}

  & > * {
    width: 50%;
    display: flex;
    flex-direction: column;

    ${mediaMax.md`
      width: 100%;
    `}

    &:not(:last-child) {
      margin-bottom: ${({ columnMarginBottom = 20 }) =>
        `${columnMarginBottom}px`};
    }

    ${media.md`
      ${({ gutter }: ResTwoColProps) => {
        if (!gutter) {
          return ``
        }

        const value = typeof gutter === 'number' ? `${gutter}px` : gutter

        return `
          padding: 0 calc(${value}/2);
        `
      }}
    `}
  }
`

export const SpacedColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > *:not(:last-child) {
    margin-bottom: 40px;
  }
`

export const ImageColumn = styled(SpacedColumn)`
  & > *:first-child {
    height: 90%;
    max-height: 780px;
    width: 100%;

    ${media.md`
      height: 100%;
      width: 85%;
    `}
  }
`
